import React, { useState, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { format, parse } from 'date-fns';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import ListPage, { ListPageRef } from '../../../sharedModules/listPage/ListPage';
import ImportHomeInspectionJobModal from '../../inspectionReport/components/ImportHomeInspectionJobModal';
import axios from '../../../sharedModules/utils/axios.utils';
import { SelectOption } from '../../../sharedModules/form/model';
import { formatDate } from '../../../utils/formatDate';
import { useGet } from '../../../sharedModules/hooks/useQuery';

const formatSortTime = (value: string | null | undefined): string => {
  if (!value) return '';
  try {
    const date = parse(value, 'HH:mm:ss', new Date());
    return format(date, 'h:mm a');
  } catch {
    return value;
  }
};

const statusConfig: Record<string, { label: string; color: 'default' | 'primary' | 'success' | 'warning' | 'info' }> = {
  open: { label: 'Open', color: 'primary' },
  contacted: { label: 'Contacted', color: 'info' },
  readyinv: { label: 'Ready to Invoice', color: 'warning' },
  review: { label: 'Review', color: 'warning' },
  complete: { label: 'Completed', color: 'success' },
  completed: { label: 'Completed', color: 'success' },
};

const getStatusChip = (status: string) => {
  const config = statusConfig[status.toLowerCase()] || { label: status, color: 'default' as const };
  return <Chip label={config.label} color={config.color} size="small" variant="outlined" />;
};

interface JobRow {
  id: number;
  sort_time?: string | null;
  customer_name?: string;
  emp_id?: string | null;
  emp_id_secondary?: string | null;
  order_no?: string;
  status?: string;
  inspection_report_id?: number | null;
}

const formatTechnicians = (row: JobRow): string =>
  [row.emp_id, row.emp_id_secondary].filter((id): id is string => !!id).join(', ');

const isCompleted = (status?: string) => {
  const s = (status || '').toLowerCase();
  return s === 'complete' || s === 'completed';
};

const jobColumns = [
  {
    name: 'Time',
    selector: 'sort_time',
    sortable: true,
    grow: 0.5,
    format: (row: JobRow) => formatSortTime(row.sort_time),
  },
  {
    name: 'Customer',
    selector: 'customer_name',
    sortable: true,
    grow: 1.25,
  },
  {
    name: 'Technician',
    selector: 'emp_id',
    sortable: true,
    grow: 1,
    cell: (row: JobRow) => formatTechnicians(row),
  },
  {
    name: 'Order #',
    selector: 'order_no',
    sortable: true,
    grow: 0.75,
  },
  {
    name: 'Status',
    selector: 'status',
    sortable: true,
    grow: 0.75,
    cell: (row: JobRow) => getStatusChip(row.status || ''),
  },
  {
    name: '',
    selector: 'inspection_report_id',
    sortable: false,
    grow: 0,
    width: '140px',
    cell: (row: JobRow) =>
      isCompleted(row.status) && row.inspection_report_id ? (
        <Button
          size="small"
          variant="contained"
          color="success"
          sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem', padding: '5px' }}
          onClick={async (e: React.MouseEvent) => {
            e.stopPropagation();
            const response = await axios.get(`/home_services/inspection_reports/${row.inspection_report_id}/pdf`, {
              responseType: 'blob',
            });
            const blobUrl = URL.createObjectURL(response.data);
            window.open(blobUrl, '_blank');
          }}
        >
          Review Report
        </Button>
      ) : null,
  },
];

const todayFormatted = formatDate(new Date().toISOString());

const clearFilterSetting = {
  customer_name_i_cont: '',
  order_no_i_cont: '',
  emp_id_or_emp_id_secondary_eq: '',
};

const UpcomingJobsPage: React.FC = () => {
  const [importModalOpen, setImportModalOpen] = useState(false);
  const listRef = useRef<ListPageRef | null>(null);
  const history = useHistory();

  const handleRowClick = useCallback(
    async (row: JobRow) => {
      if (isCompleted(row.status) && row.inspection_report_id) {
        const response = await axios.get(`/home_services/inspection_reports/${row.inspection_report_id}/pdf`, {
          responseType: 'blob',
        });
        const blobUrl = URL.createObjectURL(response.data);
        window.open(blobUrl, '_blank');
      } else if (!isCompleted(row.status)) {
        history.push(`/home_services/upcoming_inspections/${row.id}`);
      }
    },
    [history],
  );

  const { data: technicianOptions } = useGet<{ technician_options: SelectOption[] }>({
    url: '/home_services/inspection_jobs/new',
  });

  const handleImportSuccess = () => {
    listRef.current?.refetch();
    setImportModalOpen(false);
  };

  const jobFilters = [
    {
      label: 'Technician',
      type: 'select',
      param: 'emp_id_or_emp_id_secondary_eq',
      choices: technicianOptions?.data?.technician_options || [],
      width: 2,
    },
  ];

  return (
    <Grid container>
      <ImportHomeInspectionJobModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onSuccess={handleImportSuccess}
      />
      <Grid item xs={12}>
        <ListPage
          ref={listRef}
          clickableRows
          clearedFilterSetting={clearFilterSetting}
          columns={jobColumns}
          controller="home_services/inspection_jobs"
          defaultFilters={{}}
          defaultSort="sort_time asc"
          filterDefinitions={jobFilters}
          includeFilterBar
          queryPlaceholder="Search by Customer or Order #"
          title="Home Services"
          subtitle={`Today's Inspections (${todayFormatted})`}
          onRowClick={handleRowClick}
          button={
            <Button variant="contained" color="primary" onClick={() => setImportModalOpen(true)}>
              Import Work Order
            </Button>
          }
        />
      </Grid>
    </Grid>
  );
};

export default UpcomingJobsPage;
